import { UnitType } from "@kuika/kuika-cl-model/src/models/screen-canvas-sizes";
import * as React from "react";
import { GuideLineType } from "./guide-line";

export interface IHorizontalRulerProps {
  width: number;
  zoomScale: number;
  unit: UnitType;
  offset?: number;
  onCreateGuideLine?: (position: number, type: GuideLineType) => void;
  onPreviewGuideLine?: (position: number, type: GuideLineType) => void;
  onCancelPreview?: () => void;
}

export interface IHorizontalRulerState {
  isDraggingNewGuide: boolean;
  dragStartY: number;
  lastPreviewPosition: number | null;
  iframeWidth: number;
}

export class HorizontalRuler extends React.Component<IHorizontalRulerProps, IHorizontalRulerState> {
  private rulerRef = React.createRef<HTMLDivElement>();

  private resizeObserver: ResizeObserver | null = null;

  private scrollAttachTimer: number | null = null;

  constructor(props: IHorizontalRulerProps) {
    super(props);
    this.state = {
      isDraggingNewGuide: false,
      dragStartY: 0,
      lastPreviewPosition: null,
      iframeWidth: 0
    };
  }

  shouldComponentUpdate(nextProps: IHorizontalRulerProps, nextState: IHorizontalRulerState): boolean {
    return (
      nextProps.width !== this.props.width ||
      nextProps.zoomScale !== this.props.zoomScale ||
      nextProps.unit !== this.props.unit ||
      nextProps.offset !== this.props.offset ||
      nextState.isDraggingNewGuide !== this.state.isDraggingNewGuide ||
      nextState.iframeWidth !== this.state.iframeWidth
    );
  }

  componentDidMount() {
    this.updateIframeWidth();
    this.setupResizeObserver();
    this.attachScrollListener();
  }

  updateIframeWidth = () => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    if (iframe) {
      const iframeWidth = iframe.offsetWidth;
      if (iframeWidth !== this.state.iframeWidth) {
        this.setState({ iframeWidth });
      }
    }
  };

  setupResizeObserver = () => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    if (iframe && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateIframeWidth();
      });
      this.resizeObserver.observe(iframe);
    }
  };

  private scrollListener: (() => void) | null = null;

  attachScrollListener = () => {
    const tryAttach = () => {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

      if (reportWrapper && !this.scrollListener) {
        this.scrollListener = () => {
          this.forceUpdate();
        };
        reportWrapper.addEventListener("scroll", this.scrollListener, { passive: true });
      }
    };

    tryAttach();
    if (!this.scrollAttachTimer) {
      this.scrollAttachTimer = window.setInterval(tryAttach, 500);
    }
  };

  detachScrollListener = () => {
    if (this.scrollListener) {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

      if (reportWrapper) {
        reportWrapper.removeEventListener("scroll", this.scrollListener as any);
      }
      this.scrollListener = null;
    }
  };

  convertPxToUnit = (px: number): number => {
    switch (this.props.unit) {
      case UnitType.MM:
        return px * 0.264583;
      case UnitType.INCH:
        return px / 96;
      case UnitType.PT:
        return px * 0.75; // 1px = 0.75pt (96 DPI / 72 points per inch)
      default:
        return px;
    }
  };

  getUnitStep = (): number => {
    switch (this.props.unit) {
      case UnitType.MM:
        return 10;
      case UnitType.INCH:
        return 1;
      case UnitType.PT:
        return 50;
      default:
        return 50;
    }
  };

  getPixelStep = (): number => {
    switch (this.props.unit) {
      case UnitType.MM:
        return 37.8;
      case UnitType.INCH:
        return 96;
      case UnitType.PT:
        return 66.67; // 50pt * (96/72) = 66.67px
      default:
        return 50;
    }
  };

  getActualScrollOffset = (): number => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

    if (reportWrapper) {
      return reportWrapper.scrollLeft || 0;
    }

    return this.props.offset || 0;
  };

  getVerticalScrollOffset = (): number => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

    if (reportWrapper) {
      return reportWrapper.scrollTop || 0;
    }

    return 0;
  };

  renderTicks = () => {
    const { width, zoomScale } = this.props;
    const offset = this.getActualScrollOffset();
    const pixelStep = this.getPixelStep();
    const unitStep = this.getUnitStep();
    const scaledStep = pixelStep * zoomScale;
    const smallStep = scaledStep / 5; // Small ticks every 1/5 of the main step
    const ticks: React.ReactNode[] = [];

    const scaledOffset = offset * zoomScale;
    const start = Math.max(0, Math.floor(scaledOffset / smallStep) * smallStep - smallStep);
    const end = width + scaledOffset + smallStep;

    for (let pos = start; pos <= end; pos += smallStep) {
      const left = pos - scaledOffset;
      if (left < 0 || left > width) continue;

      const unitValue = this.convertPxToUnit(pos / zoomScale);
      const roundedValue = Math.round(unitValue / (unitStep / 5)) * (unitStep / 5);
      const isMainTick = Math.abs(roundedValue % unitStep) < 0.001;
      const isMajorTick = isMainTick && Math.abs(roundedValue % (unitStep * 5)) < 0.001;

      let tickHeight = "4px";
      if (isMainTick) tickHeight = "10px";
      if (isMajorTick) tickHeight = "14px";

      ticks.push(
        <div
          key={pos}
          className="ruler-tick"
          style={{
            left: `${left}px`,
            height: tickHeight,
            width: isMainTick ? "1.25px" : "0.75px",
            opacity: isMainTick ? 0.9 : 0.5,
            backgroundColor: isMainTick ? "var(--ruler-tick-strong)" : undefined
          }}
        >
          {isMajorTick && <span className="ruler-label">{this.formatNumber(roundedValue)}</span>}
          {isMainTick && !isMajorTick && roundedValue !== 0 && (
            <span className="ruler-label ruler-label-small">{this.formatNumber(roundedValue)}</span>
          )}
        </div>
      );
    }

    return ticks;
  };

  formatNumber = (value: number): string => {
    if (this.props.unit === UnitType.INCH) {
      return value.toFixed(1);
    }
    return Math.round(value).toString();
  };

  getIsometricMode = (): boolean => {
    const storedValue = localStorage.getItem("isFreePositionAreaEnabled");
    if (storedValue !== null) {
      return storedValue !== "true";
    }
    return true;
  };

  snapToIsometricGrid = (value: number): number => {
    if (!this.getIsometricMode()) {
      return value;
    }
    return Math.round(value / 10) * 10;
  };

  handleMouseDown = (e: React.MouseEvent) => {
    if (!this.props.onCreateGuideLine) return;

    e.preventDefault();
    e.stopPropagation();

    const rulerRect = this.rulerRef.current?.getBoundingClientRect();
    const scrollOffset = this.getVerticalScrollOffset();
    let initialPosition = rulerRect
      ? Math.max(0, (e.clientY - rulerRect.bottom) / this.props.zoomScale + scrollOffset)
      : 0;

    initialPosition = this.snapToIsometricGrid(initialPosition);

    this.setState({
      isDraggingNewGuide: true,
      dragStartY: e.clientY,
      lastPreviewPosition: initialPosition
    });

    if (rulerRect) {
      this.props.onPreviewGuideLine?.(initialPosition, GuideLineType.HORIZONTAL);
    }

    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseup", this.handleMouseUp);

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.addEventListener("mousemove", this.handleIframeMouseMove as any);
      iframeDoc.addEventListener("mouseup", this.handleMouseUp as any);
    }
  };

  handleMouseMove = (e: MouseEvent) => {
    if (!this.state.isDraggingNewGuide) return;

    const rulerRect = this.rulerRef.current?.getBoundingClientRect();
    if (!rulerRect) return;

    const scrollOffset = this.getVerticalScrollOffset();
    let position = Math.max(0, (e.clientY - rulerRect.bottom) / this.props.zoomScale + scrollOffset);
    position = this.snapToIsometricGrid(position);
    this.setState({ lastPreviewPosition: position });
    this.props.onPreviewGuideLine?.(position, GuideLineType.HORIZONTAL);
  };

  handleIframeMouseMove = (e: MouseEvent) => {
    if (!this.state.isDraggingNewGuide) return;

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    if (!iframe) return;

    const iframeRect = iframe.getBoundingClientRect();
    const rulerRect = this.rulerRef.current?.getBoundingClientRect();
    if (!rulerRect) return;

    const scrollOffset = this.getVerticalScrollOffset();
    const adjustedClientY = e.clientY + iframeRect.top;
    let position = Math.max(0, (adjustedClientY - rulerRect.bottom) / this.props.zoomScale + scrollOffset);
    position = this.snapToIsometricGrid(position);
    this.setState({ lastPreviewPosition: position });
    this.props.onPreviewGuideLine?.(position, GuideLineType.HORIZONTAL);
  };

  handleMouseUp = () => {
    if (this.state.isDraggingNewGuide) {
      const deltaY =
        this.state.lastPreviewPosition !== null ? Math.abs(this.state.lastPreviewPosition * this.props.zoomScale) : 0;

      if (this.state.lastPreviewPosition !== null && deltaY > 5) {
        this.props.onCreateGuideLine?.(this.state.lastPreviewPosition, GuideLineType.HORIZONTAL);
      } else {
        this.props.onCancelPreview?.();
      }
    }

    this.setState({
      isDraggingNewGuide: false,
      lastPreviewPosition: null
    });
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.removeEventListener("mousemove", this.handleIframeMouseMove as any);
      iframeDoc.removeEventListener("mouseup", this.handleMouseUp as any);
    }
  };

  componentWillUnmount() {
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.removeEventListener("mousemove", this.handleIframeMouseMove as any);
      iframeDoc.removeEventListener("mouseup", this.handleMouseUp as any);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.scrollAttachTimer) {
      window.clearInterval(this.scrollAttachTimer);
      this.scrollAttachTimer = null;
    }

    this.detachScrollListener();
  }

  render() {
    const { width } = this.props;
    const { isDraggingNewGuide, iframeWidth } = this.state;

    return (
      <div
        ref={this.rulerRef}
        className="horizontal-ruler"
        style={{
          width: `${width}px`,
          minWidth: iframeWidth > 0 ? `${iframeWidth}px` : `${width}px`,
          height: "20px",
          position: "relative",
          overflow: "hidden",
          cursor: isDraggingNewGuide ? "grabbing" : "grab"
        }}
        onMouseDown={this.handleMouseDown}
      >
        {this.renderTicks()}
      </div>
    );
  }
}
